/**
 * `useApiKeys()` — TanStack Query read hook for the current user's BYOK
 * (Bring Your Own Key) provider credentials.
 *
 * AC-1515 (epic AC-1493 — BYOK LLM credentials). Read-side companion to
 * `useApiKeyMutations` (create / replace / delete / test).
 *
 * ## Endpoint
 *
 * `GET /api/v1/credentials` — returns one row per supported provider
 * (OpenAI, Anthropic, Google) regardless of whether the user has configured
 * one. Rows carry a `has_key` flag plus non-secret metadata
 * (display_label, last_validated_at, created_at). The raw key is never
 * returned by GET. The UI uses the full list so it can render a card for
 * every provider — including "not configured" — without a separate
 * "supported providers" call.
 *
 * ## Cache key
 *
 * `queryKeys.apiKeys()` → `['apiKeys']`. The list is user-scoped
 * (auth-derived), so no ID threads through the key. Mutations on any
 * single provider (AC-1515 P7B) invalidate this single key and every
 * subscriber re-renders with fresh `has_key` flags.
 *
 * Mutations also invalidate `queryKeys.llmCatalog()` because the
 * server-side catalog response's per-model `available` flags depend on
 * which provider keys the user has configured (BYOK mode).
 */

import { useQuery, UseQueryOptions, UseQueryResult } from '@tanstack/react-query';

import apiService from '../../services/api';
import { UserApiKey } from '../../types';
import { queryKeys } from './queryKeys';

export type UseApiKeysOptions = Omit<
  UseQueryOptions<UserApiKey[], Error, UserApiKey[], ReturnType<typeof queryKeys.apiKeys>>,
  'queryKey' | 'queryFn'
>;

/**
 * Returns the standard `UseQueryResult<UserApiKey[]>`.
 *
 * @example
 *   const { data: keys, isLoading } = useApiKeys();
 *   const openaiKey = keys?.find((k) => k.provider === 'openai');
 *
 * @example
 *   // In BYOK gating logic:
 *   const { data: keys } = useApiKeys();
 *   const configuredProviders = new Set(
 *     (keys ?? []).filter((k) => k.has_key).map((k) => k.provider),
 *   );
 */
export function useApiKeys(
  options?: UseApiKeysOptions,
): UseQueryResult<UserApiKey[], Error> {
  return useQuery<UserApiKey[], Error, UserApiKey[], ReturnType<typeof queryKeys.apiKeys>>({
    queryKey: queryKeys.apiKeys(),
    queryFn: () => apiService.listApiKeys(),
    ...options,
  });
}

export default useApiKeys;
